import React from 'react';
import styles from './GameModals.module.scss';

export const DrawModal = ({ isOpen, onAccept, onDeny }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.icon}>🤝</span>
          <h2 className={styles.title}>Draw Request</h2>
          <p className={styles.subtitle}>Your opponent wants to end in a draw</p>
        </div>
        <div className={styles.content}>
          <p className={styles.message}>
            Do you accept the draw offer?
          </p>
        </div>
        <div className={styles.actions}>
          <button
            className={`${styles.button} ${styles.successButton}`}
            onClick={onAccept}
          >
            Accept Draw
          </button>
          <button
            className={`${styles.button} ${styles.secondaryButton}`}
            onClick={onDeny}
          >
            Keep Playing
          </button>
        </div>
      </div>
    </div>
  );
};

export const RematchModal = ({ isOpen, onAccept, onDeny, opponentUsername }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.icon}>🔄</span>
          <h2 className={styles.title}>Rematch Request</h2>
          <p className={styles.subtitle}>
            {opponentUsername ? `${opponentUsername} wants to play again` : 'Your opponent wants to play again'}
          </p>
        </div>
        <div className={styles.content}>
          <p className={styles.message}>
            Ready for another round?
          </p>
          <p className={styles.timeout}>Request expires in 60 seconds</p>
        </div>
        <div className={styles.actions}>
          <button
            className={`${styles.button} ${styles.primaryButton}`}
            onClick={onAccept}
          >
            Let's Go!
          </button>
          <button
            className={`${styles.button} ${styles.secondaryButton}`}
            onClick={onDeny}
          >
            No Thanks
          </button>
        </div>
      </div>
    </div>
  );
};

export const WinnerModal = ({
  isOpen,
  winner,
  currentUserId,
  currentUsername,
  opponent,
  score,
  onLeave,
  onRematch,
  onAcceptRematch,
  onDenyRematch,
  forfeit = false,
  eloChanges = null,
  opponentConnected = true,
  rematchRequested = false,
  rematchPending = false
}) => {
  if (!isOpen) return null;

  const isDraw = winner === 'draw' || winner === null;
  // Ensure type consistency when comparing winner to currentUserId
  const isWinner = winner != null && String(winner) === String(currentUserId);
  const isLoser = !isDraw && !isWinner;

  const myEloChange = eloChanges?.[currentUserId];
  const opponentEloChange = eloChanges?.[opponent?.userId];

  const getIcon = () => {
    if (isDraw) return '🤝';
    if (isWinner) return '🏆';
    return '😔';
  };

  const getTitle = () => {
    if (isDraw) return "It's a Draw!";
    if (isWinner) return 'Victory!';
    return 'Defeat';
  };

  const getSubtitle = () => {
    if (isDraw) return 'Well played by both sides';
    if (isWinner) {
      if (forfeit) return `${opponent?.username || 'Opponent'} forfeited - You win!`;
      return 'You dominated the grid!';
    }
    if (isLoser) {
      if (forfeit) return 'You forfeited the game';
      return 'Better luck next time';
    }
    return 'Game Over';
  };

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${styles.winnerModal}`}>
        <div className={styles.header}>
          <span className={styles.icon}>{getIcon()}</span>
          <h2 className={styles.title}>{getTitle()}</h2>
          <p className={styles.subtitle}>{getSubtitle()}</p>
        </div>

        {score && (
          <div className={styles.scoreDisplay}>
            <div className={styles.playerScore}>
              <div className={styles.playerName}>You</div>
              <div className={styles.score}>{score[currentUserId] || 0}</div>
              {isWinner && !isDraw && (
                <div className={styles.winnerBadge}>Winner</div>
              )}
              {myEloChange && (
                <>
                  <div className={styles.eloChange}>
                    <span className={styles.eloLabel}>Sport:</span>
                    <span className={styles.eloRating}>{myEloChange.sportRating?.new || myEloChange.newRating || '—'}</span>
                    <span className={(myEloChange.sportRating?.change || myEloChange.change || 0) >= 0 ? styles.eloPositive : styles.eloNegative}>
                      ({(myEloChange.sportRating?.change || myEloChange.change || 0) >= 0 ? '+' : ''}{myEloChange.sportRating?.change || myEloChange.change || 0})
                    </span>
                  </div>
                  {myEloChange.overallRating && (
                    <div className={styles.eloChange}>
                      <span className={styles.eloLabel}>Overall:</span>
                      <span className={styles.eloRating}>{myEloChange.overallRating.new}</span>
                      <span className={myEloChange.overallRating.change >= 0 ? styles.eloPositive : styles.eloNegative}>
                        ({myEloChange.overallRating.change >= 0 ? '+' : ''}{myEloChange.overallRating.change})
                      </span>
                    </div>
                  )}
                  {myEloChange.isProvisional && (
                    <div className={styles.provisionalBadge}>
                      Provisional ({myEloChange.sportGamesPlayed}/20)
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={styles.playerScore}>
              <div className={styles.playerName}>{opponent?.username || 'Opponent'}</div>
              <div className={styles.score}>{score[opponent?.userId] || 0}</div>
              {!isWinner && !isDraw && (
                <div className={styles.winnerBadge}>Winner</div>
              )}
              {opponentEloChange && (
                <>
                  <div className={styles.eloChange}>
                    <span className={styles.eloLabel}>Sport:</span>
                    <span className={styles.eloRating}>{opponentEloChange.sportRating?.new || opponentEloChange.newRating || '—'}</span>
                    <span className={(opponentEloChange.sportRating?.change || opponentEloChange.change || 0) >= 0 ? styles.eloPositive : styles.eloNegative}>
                      ({(opponentEloChange.sportRating?.change || opponentEloChange.change || 0) >= 0 ? '+' : ''}{opponentEloChange.sportRating?.change || opponentEloChange.change || 0})
                    </span>
                  </div>
                  {opponentEloChange.overallRating && (
                    <div className={styles.eloChange}>
                      <span className={styles.eloLabel}>Overall:</span>
                      <span className={styles.eloRating}>{opponentEloChange.overallRating.new}</span>
                      <span className={opponentEloChange.overallRating.change >= 0 ? styles.eloPositive : styles.eloNegative}>
                        ({opponentEloChange.overallRating.change >= 0 ? '+' : ''}{opponentEloChange.overallRating.change})
                      </span>
                    </div>
                  )}
                  {opponentEloChange.isProvisional && (
                    <div className={styles.provisionalBadge}>
                      Provisional ({opponentEloChange.sportGamesPlayed}/20)
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className={styles.actions}>
          {/* Show different buttons based on rematch state */}
          {rematchRequested && onAcceptRematch && onDenyRematch ? (
            // Opponent sent rematch request - show Accept/Decline
            <>
              <button
                className={`${styles.button} ${styles.primaryButton}`}
                onClick={onAcceptRematch}
              >
                ✅ Accept Rematch
              </button>
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={onDenyRematch}
              >
                ❌ Decline
              </button>
            </>
          ) : rematchPending ? (
            // Waiting for opponent to respond
            <button
              className={`${styles.button} ${styles.primaryButton} ${styles.disabled}`}
              disabled
            >
              ⏳ Waiting for opponent...
            </button>
          ) : onRematch && opponentConnected ? (
            // Initial state - show Rematch button
            <button
              className={`${styles.button} ${styles.primaryButton}`}
              onClick={onRematch}
            >
              🔄 Rematch
            </button>
          ) : null}

          {!rematchRequested && (
            <button
              className={`${styles.button} ${styles.secondaryButton}`}
              onClick={onLeave}
            >
              Leave Game
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
